// BUILD-046 tasks 8.16 / 8.22, rebuilt in correction cycle 1bb88b3e (stage B2a, item N9): the Shared tab - the unclaimed queue the Caller can take work from,
// with Take task (design "Group / Shared view and Take task"; Req 3.7, 3.19, 4.2, 4.6, 4.25, 4.26, 7.3 - 7.5, 7.10, 7.11).
//
// Rows are the same compact row as Mine and Team (TaskRow). Shared emphasises ONE action, "Take task". The list holds only unclaimed Shared_Tasks the
// Caller is a Queue_Eligible_Employee of (the server decides, live); an oversight view of someone else's task never appears here. A lost claim shows WHO
// took it (from the 409 payload) in a notice that stays after the row is gone, and the queue reloads. A task issued to a named group says so ("Anyone
// currently in <Group> can take this", task 8.22: the queue is live). "Requests I issued" is its own subview of the page (IssuedRequestsPanel), not a
// part of this list, so requested work is never mixed into the queue.
//
// The list state (loading / error / empty / data, with its own Retry) is passed in by the host, which also passes `derive` (the search and the sort over
// the loaded tasks). Take task logic is groupSharedModel.ts (tested). Nothing here parses a date-only string with `new Date()`: a row reads Overdue by THE one rule
// (`isOverdueTask`, overdueRule.ts) - an open, live, fully shown task with a Task due date before the Phoenix day, or the server's `overdue` flag. For these UNCLAIMED tasks
// that never carry the flag it is the date that decides (Owner-approved 2026-10-20; a display rule only, the counts are not computed from it).
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { notifySharedTasksChanged, onCallerChanged, workApi } from '../../api/workShared';
import type { WorkTask } from '../../api/workShared';
import { describeList } from './listStateModel';
import type { ListState } from './listStateModel';
import { describeTake, dismissTakeNotice, initialTakeState, performTake, resetTakeState } from './groupSharedModel';
import type { TakeState, TakeStore } from './groupSharedModel';
import { queueWording, recipientGroupLabelOf, recipientPeopleOf } from './taskRowModel';
// D1 display cleanup: the title is shown without the "(FICTIONAL)" test marker. Increment 1b-i: the people a task was sent to are initials circles (the full name on hover, keyboard focus and
// tap: PersonAvatar.tsx); a group name stays text.
import { stripTestMarker } from './displayNames';
import { AvatarGroup } from './PersonAvatar';
// Cycle A (2026-10-21): the same one rule (overdueRule.ts) as every other row, judged against the page's one Phoenix-day signal (usePhoenixDay).
import { isOverdueTask } from './overdueRule';
import { usePhoenixDay } from './usePhoenixDay';
import { ListStateView } from './ListStateView';
import { OriginChip, hasVisibleOrigin } from './OriginChip';
import { RequesterBadge } from './RequesterBadge';
import { badgeForTask } from './requesterBadgeModel';
import { comfortableRequesterShown, showRequesterComfortable } from './showRequesterStore';
import { ActionsCell, Cell, DueMark, PriorityMark, QuietLine, TaskCell, TaskTable, TaskTableRow } from './TaskTable';
import { jobOf } from './jobDisplayModel';
import { customerOf } from './customerDisplayModel';
import { associationParts } from './CustomerParts';
import { useCustomersInPlay } from './useCustomerColumn';
import type { Density } from './densityStore';

export interface SharedViewProps {
  /** The `shared` list of the host's lists controller. */
  state: ListState<WorkTask>;
  /** `me.simulated`: the source adapters are simulated, so the origin chip carries SIMULATED. */
  adapterSimulated: boolean;
  density: Density;
  /** The rows to draw: the loaded tasks after the search and the sort (viewListModel). */
  rows: readonly WorkTask[];
  /** True while a search narrows the list (so an empty result says so). */
  narrowed: boolean;
  /** Shown instead of the usual empty line when a Customer / Job or Customer filter is on and leaves no row (it names the filter and offers to clear it). */
  emptyFilterNote?: ReactNode;
  /** Retry for THIS list only. */
  onRetry: () => void;
  /** After an accepted claim: the host refreshes My tasks (the task is the Caller's now) and this list. */
  onTaken: (item: WorkTask) => void;
  /** Reload this list (after a lost or refused claim: the task is no longer, or never was, takeable). */
  onReload: () => void;
}

export function SharedView({ state, adapterSimulated, density, rows, narrowed, emptyFilterNote, onRetry, onTaken, onReload }: SharedViewProps) {
  const phoenixDay = usePhoenixDay();
  // CHANGED 2026-10-25: while customers are in play, a row with a job and no customer writes "Job: <name>" (associationParts), so a job never reads as a customer.
  const customersInPlay = useCustomersInPlay();
  const [take, setTake] = useState<TakeState>(initialTakeState());
  const latest = useRef<TakeState>(take);
  const store = useRef<TakeStore>({
    get: () => latest.current,
    set: (next) => {
      latest.current = next;
      setTake(next);
    },
  }).current;
  const handlers = useRef({ onTaken, onReload });
  handlers.current = { onTaken, onReload };

  // A Caller change forgets every saving state and notice of the previous Caller.
  useEffect(() => onCallerChanged(() => store.set(resetTakeState())), [store]);

  const view = describeList(state, 'Group / Shared tasks', 'Nothing is waiting in the shared queue for you.');

  async function takeTask(task: WorkTask): Promise<void> {
    const outcome = await performTake(workApi, store, task.id, stripTestMarker(task.title));
    if (outcome.kind === 'taken') {
      notifySharedTasksChanged({ reason: 'claim', id: task.id });
      handlers.current.onTaken(outcome.item as WorkTask);
    } else if (outcome.kind === 'lost' || (outcome.kind === 'failed' && outcome.reload)) {
      // 9.20 (browser run): the queue is reloaded because the task is no longer takeable, so the Shared queue count follows it (it was left at the old number).
      notifySharedTasksChanged({ reason: 'claim-not-taken', id: task.id });
      handlers.current.onReload();
    }
  }

  return (
    <div className="space-y-3" data-testid="group-shared-view">
      {take.notices.length > 0 && (
        <ul className="space-y-1" aria-label="Notices">
          {take.notices.map((notice) => (
            <li key={notice.id} role="status" aria-live="polite" className="flex items-start justify-between gap-2 text-sm bg-amber-50 text-amber-900 border border-amber-200 rounded px-3 py-2">
              <span>{notice.text}</span>
              <button type="button" onClick={() => store.set(dismissTakeNotice(latest.current, notice.id))} aria-label="Dismiss notice" className="font-bold">
                &times;
              </button>
            </li>
          ))}
        </ul>
      )}

      <ListStateView
        view={view}
        listLabel="Group / Shared tasks"
        onRetry={onRetry}
        renderItems={() => {
          const shown = rows;
          if (shown.length === 0 && emptyFilterNote) return <>{emptyFilterNote}</>;
          if (shown.length === 0) return <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">{narrowed ? 'No shared tasks match the search.' : 'Nothing is waiting in the shared queue for you.'}</div>;
          return (
            <TaskTable variant="mine" ariaLabel="Shared tasks you can take">
              {shown.map((task) => {
                const control = describeTake(take, task.id, { status: task.status, ownerEmployeeId: task.ownerEmployeeId, unclaimed: task.unclaimed, redacted: task.redacted, lifecycle: task.lifecycle }, 'shared');
                const overdue = isOverdueTask(task, phoenixDay);
                const recipientGroups = recipientGroupLabelOf(task);
                const recipientPeople = recipientPeopleOf(task);
                // The recorded live-queue label for a task issued to a named group (Decision L1): new members can take it, removed members cannot.
                const wording = queueWording(task);
                const compact = density === 'compact';
                // "To <group> [circles]": a group is text, the people it was sent to are circles (no name beside them). The live-queue sentence below already names the group, so this is shown only when
                // there is no such sentence. In Compact it sits right after the title, outside the cut-off quiet line (that line hides overflow, which would clip a circle's focus ring).
                const toRecipients =
                  (recipientGroups !== null || recipientPeople.length > 0) && wording === null ? (
                    <span key="to" data-testid="shared-recipients" className="inline-flex shrink-0 items-center gap-x-1 text-xs text-gray-600">
                      <span>{recipientGroups !== null ? `To ${recipientGroups}` : 'To'}</span>
                      <AvatarGroup people={recipientPeople} size="compact" label="Sent to" />
                    </span>
                  ) : null;
                // The requester is shown whenever one is recorded (the rule this line always had, as "From <name>"): now the short badge.
                const badge = badgeForTask(task, typeof task.requesterName === 'string' && task.requesterName.trim() !== '' && comfortableRequesterShown(density, showRequesterComfortable.get()));
                return (
                  <TaskTableRow
                    key={task.id}
                    variant="mine"
                    id={`shared-${task.id}`}
                    density={density}
                    requested={badge}
                    job={jobOf(task)}
                    customer={customerOf(task)}
                    below={
                      // Increment 1a (D): Compact is ONE tight row, so the live-queue sentence is inline in the row (below, in the Task cell) and not a second line; only a failure to take the task is a line below.
                      control.failure !== null ? (
                        <p role="alert" className="mt-1 text-xs text-red-700">
                          {control.failure.headline}
                          {control.failure.detail !== null && <span className="ml-1">{control.failure.detail}</span>}
                        </p>
                      ) : undefined
                    }
                  >
                    <TaskCell
                      title={task.title}
                      density={density}
                      titleSuffix={compact ? toRecipients : undefined}
                      badge={badge !== null ? <RequesterBadge badge={badge} /> : null}
                      secondary={
                        <QuietLine
                          parts={[
                            // BUILD-046 J1a: the Customer, then the Job, are the FIRST parts in Comfortable; Compact has its own column.
                            ...associationParts(task, density, { customersInPlay }),
                            compact ? null : toRecipients,
                            task.reassignmentNeeded === true ? 'Reassignment needed' : null,
                            hasVisibleOrigin(task) ? <OriginChip key="origin" item={task} adapterSimulated={adapterSimulated} /> : null,
                            // Compact: the sentence is the last part of the one line (cut with an ellipsis; the whole sentence is its hover text). Comfortable has it as the quiet detail line.
                            compact && wording !== null ? (
                              <span key="wording" data-testid="queue-wording" title={wording}>
                                {wording}
                              </span>
                            ) : null,
                          ]}
                        />
                      }
                      detail={wording !== null ? <span data-testid="queue-wording">{wording}</span> : undefined}
                    />
                    <Cell stackedLabel="Priority">
                      <PriorityMark priority={task.priority} />
                    </Cell>
                    <Cell stackedLabel="Due">
                      <DueMark dueDate={task.redacted === true ? null : task.dueDate} overdue={overdue} />
                    </Cell>
                    <ActionsCell>
                      {control.lost !== null && <span className="text-sm text-amber-800">{control.lost}</span>}
                      {control.visible && (
                        <button
                          type="button"
                          disabled={control.disabled}
                          onClick={() => void takeTask(task)}
                          aria-label={`Take task: ${stripTestMarker(task.title)}`}
                          className="inline-flex items-center rounded-md bg-[#1f6fa5] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#195d8a] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#2a7fb8]"
                        >
                          {control.label}
                        </button>
                      )}
                    </ActionsCell>
                  </TaskTableRow>
                );
              })}
            </TaskTable>
          );
        }}
      />
    </div>
  );
}
